import React, { useContext, useEffect, useRef } from 'react';

import { ReportsDefaultContext } from 'pages/reports/context/ReportsDefaultContext';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { Option, Report, ReportApi, ReportFilter } from 'apis/ReportApi';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { useSuperState } from 'hooks';
import { Col } from 'react-bootstrap';

interface ReportDropdownListProps {
    data?: Option[];
    filter: ReportFilter;
    filterIndex: number;
    onOptionsLoaded?: (options: Option[]) => void;
    report: Report;
    reportFilters: ReportFilter[] | undefined;
    scopeId: number;
    scopeObjectId: number;
    setReport: (report: Report) => void;
    setReportFilters: (filters: ReportFilter[] | undefined) => void;
    urlSync?: boolean;
}

export const ReportDropdownList = ({
    data,
    filter,
    filterIndex,
    onOptionsLoaded,
    report,
    reportFilters,
    scopeId,
    scopeObjectId,
    setReport,
    setReportFilters,
    urlSync = true
}: ReportDropdownListProps) => {

    const { filterOptionsCache } = useContext(ReportsDefaultContext);
    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();
    const [state, setState] = useSuperState({
        filterOptions: (data ?? filter.FilterOptions ?? []) as Option[],
        isLoading: false,
        selectedFilterOption: { ProductId: '', ProductName: '', Text: 'Select', Value: '0' } as Option,
        selectedFilterOptions: [] as Option[]
    });

    const { filterOptions, isLoading, selectedFilterOption, selectedFilterOptions } = state;

    const isMultiSelect = filter.MultiSelect;
    const hasParentDependency = !!filter.ParentFilterId && filter.ParentFilterId > 0;
    const isInitialMount = useRef(true);

    // Field mapping � defaults to Value/Text, overridden per filter name
    const FILTER_FIELD_MAP: Record<string, { dataItemKey: string; textField: string }> = {
        ClassroomProducts: { dataItemKey: 'ProductId', textField: 'ProductName' },
        ClassroomTests: { dataItemKey: 'Value', textField: 'Text' }
    };
    const { dataItemKey, textField } = FILTER_FIELD_MAP[filter.Name] ?? { dataItemKey: 'Value', textField: 'Text' };

    // For filters with custom field mapping, deduplicate by the mapped key
    const setFilterOptions = (options: Option[]) => {

        if (FILTER_FIELD_MAP[filter.Name]) {

            const key = FILTER_FIELD_MAP[filter.Name].dataItemKey as keyof Option;
            const seen = new Set<string>();
            const unique = options.filter(opt => {

                const id = opt[key];
                if (!id || seen.has(id)) return false;
                seen.add(id);
                return true;

            });
            setState({ filterOptions: unique });
            onOptionsLoaded?.(unique);

        } else {

            setState({ filterOptions: options });
            onOptionsLoaded?.(options);

        }

    };

    // Derive the live parent filter value directly from the prop to avoid stale state
    const liveParentFilter = hasParentDependency
        ? reportFilters?.find(f => f.Id === filter.ParentFilterId)
        : undefined;
    const parentHasValue = !!liveParentFilter?.Value && liveParentFilter.Value !== '0';
    const parentValue = liveParentFilter?.Value ?? '';

    // When the parent passes new data, update local options (skip onOptionsLoaded
    // to avoid circular updates when FilterWrapper passes filtered data back)
    useEffect(() => {

        if (data) {

            if (FILTER_FIELD_MAP[filter.Name]) {

                const key = FILTER_FIELD_MAP[filter.Name].dataItemKey as keyof Option;
                const seen = new Set<string>();
                const unique = data.filter(opt => {

                    const id = opt[key];
                    if (!id || seen.has(id)) return false;
                    seen.add(id);
                    return true;

                });
                setState({ filterOptions: unique });

            } else {

                setState({ filterOptions: data });

            }

        }

    }, [data]);

    useEffect(() => {

        if (!hasParentDependency) return;

        // Parent cleared → reset child options and selection
        if (!parentValue || parentValue === '0') {

            if (filter.Value !== '0') {

                handleChangeFilterValue({ ProductId: '', ProductName: '', Text: 'Select', Value: '0' }, filterIndex);

            }
            setFilterOptions([]);
            setState({ selectedFilterOption: { ProductId: '', ProductName: '', Text: 'Select', Value: '0' } });
            return;

        }

        // On initial mount, skip fetch if handleParamsInUrl already populated options
        if (isInitialMount.current) {

            isInitialMount.current = false;
            if (filter.FilterOptions?.length) return;

        }

        const parentValues = parentValue.split(',').map(value => value.trim()).filter(Boolean);

        const fetchOptions = async () => {

            const uncached = parentValues.filter(pv => !filterOptionsCache.has(`${report.Id}_${scopeObjectId}_${filter.Id}_${pv}`));
            if (uncached.length > 0) {

                const optionsByParent = await ReportApi
                    .GetFilterOptions(filter, uncached.join(','), scopeId, scopeObjectId)
                    .catch(catchWithToast(formatMessage({ id: 'failed_to_load_filter_options.19927c8b', defaultMessage: 'Failed to load filter options.' })));
                if (optionsByParent) {

                    Object.entries(optionsByParent).forEach(([pv, opts]) => {

                        filterOptionsCache.set(`${report.Id}_${scopeObjectId}_${filter.Id}_${pv}`, opts ?? []);

                    });

                }

            }

            const merged: Option[] = [];
            parentValues.forEach(pv => {

                const opts = filterOptionsCache.get(`${report.Id}_${scopeObjectId}_${filter.Id}_${pv}`) ?? [];
                opts.forEach((opt: Option) => {

                    if (!merged.some(m => m.Value === opt.Value)) merged.push(opt);

                });

            });

            if (reportFilters) {

                setFilterOptions(merged);
                setState({ selectedFilterOption: { ProductId: '', ProductName: '', Text: 'Select', Value: '0' } });

            }

        };

        setState({ isLoading: true });
        fetchOptions().finally(() => setState({ isLoading: false }));

    }, [parentValue]);

    const handleChangeFilterValue = (option: Option, filterIndex: number) => {

        if (reportFilters) {

            const copyOfReportFilters = [...reportFilters];
            copyOfReportFilters[filterIndex].Value = option.Value;
            setReportFilters(copyOfReportFilters);
            if (report) {

                setReport({ ...report, ReportFilters: copyOfReportFilters });

            }
            setState({ selectedFilterOption: option });

        }

        if (urlSync) {

            const url = new URL(window.location.href);
            const search_params = url.searchParams;
            search_params.set(filter.Name ? filter.Name : filter.DisplayName, option.Value);
            url.search = search_params.toString();
            window.history.pushState(null, '', url);

        }

    };

    const handleChangeMultiFilterValue = (options: Option[], filterIndex: number) => {

        if (reportFilters) {

            const copyOfReportFilters = [...reportFilters];
            copyOfReportFilters[filterIndex].Value = options.map(o => o.Value).join(',');
            setReportFilters(copyOfReportFilters);
            if (report) {

                setReport({ ...report, ReportFilters: copyOfReportFilters });

            }
            setState({ selectedFilterOptions: options });

        }

        const allSelected = filterOptions.length > 0 && options.length >= filterOptions.length;
        const urlValue = allSelected ? 'All' : options.map(o => o.Value).join(',');
        if (urlSync) {

            const url = new URL(window.location.href);
            const search_params = url.searchParams;
            search_params.set(filter.Name ? filter.Name : filter.DisplayName, urlValue);
            url.search = search_params.toString();
            window.history.pushState(null, '', url);

        }

    };

    useEffect(() => {

        const currentValue = reportFilters?.[filterIndex]?.Value;

        // Reset: Value is empty/0 → clear dropdown display state
        if (!currentValue || currentValue === '0') {

            if (isMultiSelect) {

                if (selectedFilterOptions.length > 0) setState({ selectedFilterOptions: [] });

            } else if (selectedFilterOption.Value !== '0') {

                setState({ selectedFilterOption: { ProductId: '', ProductName: '', Text: 'Select', Value: '0' } });

            }
            return;

        }

        if (isMultiSelect) {

            const selectedValues = filter.Value?.split(',').map(value => value.trim()).filter(Boolean) ?? [];
            const matched = filterOptions.filter(o => selectedValues.includes(o.Value));
            const url = new URL(window.location.href);
            const search_params = url.searchParams;
            const currentUrlVal = search_params.get(filter.Name ? filter.Name : filter.DisplayName);
            if (currentUrlVal?.toLowerCase() === 'all') {

                if (filterOptions.length > 0) handleChangeMultiFilterValue(filterOptions, filterIndex);

            } else if (matched.length > 0) {

                setState({ selectedFilterOptions: matched });
                if (urlSync) {

                    const url = new URL(window.location.href);
                    const search_params = url.searchParams;
                    search_params.set(filter.Name ? filter.Name : filter.DisplayName, matched.map(o => o.Value).join(','));
                    url.search = search_params.toString();
                    window.history.pushState(null, '', url);

                }

            }

        } else {

            const existingFilterOption = filterOptions.find(option => option.Value === filter.Value);
            if (existingFilterOption) setState({ selectedFilterOption: existingFilterOption });
            if (urlSync) {

                const url = new URL(window.location.href);
                const search_params = url.searchParams;
                if (existingFilterOption) {

                    search_params.set(filter.Name ? filter.Name : filter.DisplayName, existingFilterOption?.Value);

                }
                url.search = search_params.toString();
                window.history.pushState(null, '', url);

            }

        }

    }, [filterOptions, filter.Value]);

    return (
        <Col className="d-flex col-12">
            <GMetrixDropdown
                limitMenuWidth
                customDisplayText={
                    isLoading
                        ? (
                            <span className="d-flex align-items-center gap-2">
                                <GMetrixLoader size="small" type="infinite-spinner" />

                                <FormattedMessage defaultMessage="Loading..." id="loading.2350c7f9" />
                            </span>
                        )
                        : hasParentDependency && !parentHasValue
                            ? formatMessage({ id: 'select_parentfilter_first.c2aa056b', defaultMessage: 'Select {parentFilter} first' }, { parentFilter: liveParentFilter?.DisplayName ?? formatMessage({ id: 'parent_filter.c1f254f9', defaultMessage: 'parent filter' }) })
                            : hasParentDependency && parentHasValue && filterOptions.length === 0
                                ? formatMessage({ id: 'no_items_available.289d4178', defaultMessage: 'No Items Available' })
                                : isMultiSelect && selectedFilterOptions.length > 0
                                    ? formatMessage({ id: 'count_selected.44440185', defaultMessage: '{count} Selected' }, { count: selectedFilterOptions.length })
                                    : undefined
                }
                disabled={isLoading || (
                    data && data.length > 0
                        ? false
                        : hasParentDependency
                            ? !parentHasValue || filterOptions.length === 0
                            : filterOptions.length === 0
                )}
                onChange={(dataItem: Option | Option[]) => {

                    if (isMultiSelect) {

                        const options = Array.isArray(dataItem) ? dataItem : dataItem ? [dataItem] : [];
                        handleChangeMultiFilterValue(options, filterIndex);

                    } else {

                        const option = Array.isArray(dataItem) ? dataItem[0] : dataItem;
                        if (option) handleChangeFilterValue(option, filterIndex);

                    }

                }}
                className="report-min-width"
                dataItemKey={dataItemKey as any}
                dataItems={filterOptions}
                label={filter.DisplayName ? filter.DisplayName : filter.Name}
                multiSelect={isMultiSelect}
                required={filter.FilterRequired}
                textField={textField as any}
                value={isMultiSelect ? selectedFilterOptions : selectedFilterOption}
            />
        </Col>
    );

};